import * as Sentry from '@sentry/nextjs';
import { TRACKED_EVENT_NAME } from '@/constants';
import { validateGtag } from '@/utils';
import { useLocale } from 'next-intl';
import { useEffect } from 'react';

/**
 * 에러 바운더리 노출 추적 — 마운트 시 Sentry 전송 + view_error_page 이벤트 발송.
 *
 * 에러 "발생" 분석은 복귀 버튼 클릭이 아니라 노출로 잡는다(에러를 보고 그냥 이탈한
 * 사용자 포함). 어느 페이지에서 났는지는 자동 수집되는 page_location 으로, 어느
 * 바운더리인지는 errorPage(snake_case — blog_tag·blog_detail·news_detail·global)로
 * 구분한다(Sentry source 태그와 동일 값 — 양쪽 조인 축).
 *
 * Sentry 에는 항상 전송하되(스택·재현 정보 확보) Slack 알림 태그(alertType:
 * 'slack-notify')는 붙이지 않는다 — 알림은 글로벌 바운더리(sendSentryError)만.
 * 컨벤션·잔여 적용 대상: docs/plans/2026-08-03-error-page-tracking-convention.md
 */
export const useErrorPageTracking = (
  errorPage: string,
  error: Error & { digest?: string },
) => {
  const locale = useLocale();

  useEffect(() => {
    Sentry.captureException(error, { tags: { source: errorPage } });

    if (!validateGtag()) return;

    window.gtag('event', TRACKED_EVENT_NAME.VIEW_ERROR_PAGE, {
      error_page: errorPage,
      error_digest: error.digest,
      user_language: locale,
    });
  }, [errorPage, error, locale]);
};
